/**
 * Pure helpers of the forgot-password flow (see forgotPassword.helpers.test.js).
 */

export const CODE_LENGTH = 6;
export const PASSWORD_MIN = 8;
export const PASSWORD_MAX = 128;

// Defaults when the API answer leaves them out.
export const DEFAULT_EXPIRES_MINUTES = 15;
export const DEFAULT_RESEND_AFTER_SECONDS = 60;

// Errors after which the code can no longer be used: the user asks for a new one.
export const RESTART_CODES = Object.freeze(['CODE_EXPIRED', 'TOO_MANY_ATTEMPTS']);

export const MESSAGES = Object.freeze({
    INVALID_EMAIL: 'Ingresa un correo electrónico válido.',
    INCOMPLETE_CODE: `Ingresa los ${CODE_LENGTH} dígitos del código.`,
    INVALID_CODE: 'El código no es correcto. Revísalo e inténtalo de nuevo.',
    CODE_EXPIRED: 'El código expiró. Solicita uno nuevo para continuar.',
    TOO_MANY_ATTEMPTS: 'Superaste el número de intentos. Solicita un código nuevo para continuar.',
    WEAK_PASSWORD: `La contraseña debe tener entre ${PASSWORD_MIN} y ${PASSWORD_MAX} caracteres.`,
    PASSWORD_SHORT: `La contraseña debe tener al menos ${PASSWORD_MIN} caracteres.`,
    PASSWORD_MISMATCH: 'Las contraseñas no coinciden.',
    RATE_LIMITED: 'Demasiados intentos. Espera unos minutos e inténtalo de nuevo.',
    NETWORK: 'No pudimos conectar con el servidor. Revisa tu conexión e inténtalo de nuevo.',
    SERVER: 'Ocurrió un problema en el servidor. Inténtalo de nuevo en unos minutos.',
    UNKNOWN: 'No pudimos completar la solicitud. Inténtalo de nuevo.'
});

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(email) {
    return EMAIL_PATTERN.test(String(email || '').trim());
}

/**
 * `ana.iturbe@gmail.com` → `an•••@gmail.com`. Short local parts keep one character.
 * @param {string} email
 */
export function maskEmail(email) {
    const value = String(email || '').trim();
    const at = value.lastIndexOf('@');
    if (at <= 0) return value;
    const local = value.slice(0, at);
    const domain = value.slice(at + 1);
    const visible = local.length > 3 ? 2 : 1;
    return `${local.slice(0, visible)}•••@${domain}`;
}

/** Only the digits of `value`, at most CODE_LENGTH of them. */
export function codeDigits(value) {
    return String(value || '')
        .replace(/\D/g, '')
        .slice(0, CODE_LENGTH);
}

/**
 * Place typed or pasted text into the code at `index`: each digit fills a box from there on.
 * Returns the new code and the index of the box to focus next.
 * @param {string} code - Current code (digits, may be shorter than CODE_LENGTH)
 * @param {number} index - Box the text went into
 * @param {string} text
 * @returns {{ code: string, focus: number }}
 */
export function fillCode(code, index, text) {
    const digits = String(text || '').replace(/\D/g, '');
    const boxes = Array.from({ length: CODE_LENGTH }, (_, i) => code[i] || '');
    if (!digits) return { code: boxes.join(''), focus: index };
    // A full code (paste, SMS/email autofill) always replaces everything.
    const start = digits.length >= CODE_LENGTH ? 0 : index;
    let i = start;
    for (const digit of digits) {
        if (i >= CODE_LENGTH) break;
        boxes[i] = digit;
        i += 1;
    }
    return { code: compact(boxes), focus: Math.min(i, CODE_LENGTH - 1) };
}

/** Boxes as a string; a hole ends the code (boxes after it are dropped). */
function compact(boxes) {
    const hole = boxes.indexOf('');
    return (hole === -1 ? boxes : boxes.slice(0, hole)).join('');
}

/**
 * Clear the box at `index` (Backspace): the code is cut there.
 * @returns {{ code: string, focus: number }}
 */
export function clearCodeAt(code, index) {
    if (index < code.length) return { code: code.slice(0, index), focus: index };
    const previous = Math.max(0, index - 1);
    return { code: code.slice(0, previous), focus: previous };
}

/** Rules shown live under the new password. */
export function passwordChecks(password, confirm) {
    const length = String(password || '').length;
    return {
        longEnough: length >= PASSWORD_MIN,
        tooLong: length > PASSWORD_MAX,
        matches: Boolean(confirm) && password === confirm
    };
}

/**
 * Read an API answer of the recovery endpoints.
 * @param {{ status: number, data: any }} res
 * @returns {{ ok: true, data: Object } | { ok: false, code: string, message: string }}
 */
export function readOutcome(res) {
    const status = res?.status;
    const data = res?.data && typeof res.data === 'object' ? res.data : {};
    if (status >= 200 && status < 300 && data.ok !== false) return { ok: true, data };

    const serverMessage = [data.message, data.error].find((m) => typeof m === 'string' && m.trim());
    if (status === 429) return { ok: false, code: 'RATE_LIMITED', message: serverMessage || MESSAGES.RATE_LIMITED };

    const code = typeof data.code === 'string' && data.code ? data.code : 'UNKNOWN';
    return { ok: false, code, message: serverMessage || MESSAGES[code] || MESSAGES.UNKNOWN };
}

/** The outcome of a request that threw (network error, timeout, 5xx). */
export function failureOutcome(error) {
    if (error?.response?.status >= 500) return { ok: false, code: 'SERVER', message: MESSAGES.SERVER };
    return { ok: false, code: 'NETWORK', message: MESSAGES.NETWORK };
}

/** `forgot`'s timing, with defaults for anything missing or invalid. */
export function readTiming(data) {
    const minutes = Number(data?.expiresMinutes);
    const seconds = Number(data?.resendAfterSeconds);
    return {
        expiresMinutes: Number.isFinite(minutes) && minutes > 0 ? Math.round(minutes) : DEFAULT_EXPIRES_MINUTES,
        resendAfterSeconds: Number.isFinite(seconds) && seconds >= 0 ? Math.round(seconds) : DEFAULT_RESEND_AFTER_SECONDS
    };
}

/** 75 → '1:15' */
export function formatCountdown(seconds) {
    const s = Math.max(0, Math.ceil(seconds));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
